import * as React from "react"
import { Button } from "@/components/ui/button"
import type { ChangeLogField, ChangeLogRow } from "@/types/change_log"

/** Beyond this, the rest collapse behind a "+N more". */
const VISIBLE = 3

/**
 * The "What changed" cell: what kind of change it was, then one line per field
 * that moved, old value to new.
 *
 * Two rules keep a log of these scannable. Every value is clamped to one line
 * with the whole thing in a `title`, so a three-hundred-character message
 * template cannot make one row four inches tall. And only the first few fields
 * show, because a save that moved eleven fields would otherwise push the rows
 * around it off the screen — the rest expand in place, which is cheaper to read
 * than a dialog and keeps the row you were looking at where it was.
 */
export function ChangeLogFields({ entry }: { entry: ChangeLogRow }) {
  const [expanded, setExpanded] = React.useState(false)

  const shown = expanded ? entry.fields : entry.fields.slice(0, VISIBLE)
  const hidden = entry.fields.length - shown.length

  return (
    <div className="min-w-0">
      <p className="m-0 text-xs text-ink-muted">{entry.kind_label}</p>

      {entry.summary && (
        <p className="m-0 mt-0.5 text-sm text-ink-body">{entry.summary}</p>
      )}

      {shown.length > 0 && (
        <dl className="m-0 mt-1 space-y-1">
          {shown.map((field) => (
            <FieldLine key={field.field} field={field} />
          ))}
        </dl>
      )}

      {hidden > 0 && (
        <Button
          variant="link"
          size="sm"
          className="mt-1 px-0"
          onClick={() => setExpanded(true)}
        >
          +{hidden} more {hidden === 1 ? "field" : "fields"}
        </Button>
      )}

      {expanded && entry.fields.length > VISIBLE && (
        <Button
          variant="link"
          size="sm"
          className="mt-1 px-0"
          onClick={() => setExpanded(false)}
        >
          Show less
        </Button>
      )}
    </div>
  )
}

function FieldLine({ field }: { field: ChangeLogField }) {
  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
      <dt className="shrink-0 text-xs text-ink-muted">{field.label}</dt>
      <dd className="m-0 flex min-w-0 flex-1 items-baseline gap-1.5">
        {field.redacted ? (
          <span className="text-ink-muted italic">changed (value not recorded)</span>
        ) : (
          <>
            <Value text={field.from} />
            <span aria-hidden="true" className="shrink-0 text-ink-muted">
              →
            </span>
            <span className="sr-only">became</span>
            <Value text={field.to} />
          </>
        )}
      </dd>
    </div>
  )
}

function Value({ text }: { text: string | null }) {
  if (text === null || text === "") {
    return (
      <span className="shrink-0 text-ink-muted" title="empty">
        —
      </span>
    )
  }

  return (
    <span className="line-clamp-1 min-w-0 break-all text-ink-body" title={text}>
      {text}
    </span>
  )
}
